import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Badge,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  ExpandMore,
  Person,
  Business,
  Email,
  Phone,
  LocationOn,
  Schedule,
  Warning,
  Security,
  TrendingUp,
  PendingActions,
  ThumbUp,
  ThumbDown,
  Timeline
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTheme } from '@mui/material/styles';

const AdminRegistrations = () => {
  const theme = useTheme();
  const [registrations, setRegistrations] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [rejectionDialog, setRejectionDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadRegistrations();
    loadStatistics();
  }, []);

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/admin/registration-requests', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRegistrations(data.data.requests || []);
      } else {
        throw new Error('Failed to load registrations');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await fetch('/api/auth/admin/registration-statistics', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data.data);
      }
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  };

  const viewRequestDetails = async (requestId) => {
    try {
      const response = await fetch(`/api/auth/admin/registration-requests/${requestId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedRequest(data.data);
      } else {
        throw new Error('Failed to load request details');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const approveRequest = async () => {
    try {
      setProcessing(true);
      const response = await fetch(`/api/auth/admin/registration-requests/${selectedRequest.id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          admin_notes: adminNotes
        })
      });

      if (response.ok) {
        const data = await response.json();
        setApprovalDialog(false);
        setSelectedRequest(null);
        setAdminNotes('');
        loadRegistrations();
        loadStatistics();
        
        // Show success message with temporary password
        alert(`User approved successfully!\nTemporary password: ${data.data.temporary_password}\n\nThis password has been emailed to the user.`);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to approve request');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const rejectRequest = async () => {
    try {
      setProcessing(true);
      const response = await fetch(`/api/auth/admin/registration-requests/${selectedRequest.id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rejection_reason: rejectionReason,
          admin_notes: adminNotes
        })
      });

      if (response.ok) {
        setRejectionDialog(false);
        setSelectedRequest(null);
        setAdminNotes('');
        setRejectionReason('');
        loadRegistrations();
        loadStatistics();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to reject request');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'expired': return 'default';
      default: return 'default';
    }
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      default: return 'default';
    }
  };

  const filteredRegistrations = registrations.filter(reg => 
    filterStatus === 'all' || reg.status === filterStatus
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        User Registration Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <PendingActions color="warning" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">{statistics.pending_count}</Typography>
                    <Typography color="text.secondary">Pending Requests</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <ThumbUp color="success" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">{statistics.approved_count}</Typography>
                    <Typography color="text.secondary">Approved</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TrendingUp color="primary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">{statistics.approval_rate}%</Typography>
                    <Typography color="text.secondary">Approval Rate</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Schedule color="info" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">{statistics.average_processing_hours}h</Typography>
                    <Typography color="text.secondary">Avg Processing Time</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filter Controls */}
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={filterStatus}
            label="Filter by Status"
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="all">All Requests</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </Select>
        </FormControl>

        <Button onClick={loadRegistrations} variant="outlined">
          Refresh
        </Button>
      </Box>

      {/* Registrations Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Applicant</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Risk Level</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRegistrations.map((registration) => (
              <TableRow key={registration.id}>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2">{registration.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {registration.email}
                    </Typography>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {registration.company || 'Not provided'}
                    </Typography>
                    {registration.job_title && (
                      <Typography variant="caption" color="text.secondary">
                        {registration.job_title}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Chip 
                    label={registration.status.toUpperCase()}
                    color={getStatusColor(registration.status)}
                    size="small"
                  />
                  {registration.is_expired && (
                    <Chip label="EXPIRED" color="error" size="small" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                
                <TableCell>
                  {registration.risk_assessment && (
                    <Chip 
                      label={registration.risk_assessment.level.toUpperCase()}
                      color={getRiskColor(registration.risk_assessment.level)}
                      size="small"
                      icon={<Security />}
                    />
                  )}
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {format(new Date(registration.requested_at), 'MMM dd, yyyy')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(registration.requested_at), 'HH:mm')}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Box display="flex" gap={1}>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small" 
                        onClick={() => viewRequestDetails(registration.id)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    
                    {registration.status === 'pending' && !registration.is_expired && (
                      <>
                        <Tooltip title="Approve">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => {
                              setSelectedRequest(registration);
                              setApprovalDialog(true);
                            }}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Reject">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => {
                              setSelectedRequest(registration);
                              setRejectionDialog(true);
                            }}
                          >
                            <Cancel />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredRegistrations.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">
            No registration requests found for the selected filter.
          </Typography>
        </Box>
      )}

      {/* Request Details Dialog */}
      <Dialog open={!!selectedRequest && !approvalDialog && !rejectionDialog} 
              onClose={() => setSelectedRequest(null)} 
              maxWidth="md" 
              fullWidth>
        {selectedRequest && (
          <>
            <DialogTitle>
              Registration Request Details
              <Chip 
                label={selectedRequest.status.toUpperCase()}
                color={getStatusColor(selectedRequest.status)}
                size="small"
                sx={{ ml: 2 }}
              />
            </DialogTitle>
            
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Personal Information
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="Name" secondary={selectedRequest.name} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Email" secondary={selectedRequest.email} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Phone" secondary={selectedRequest.phone || 'Not provided'} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Country" secondary={selectedRequest.country || 'Not provided'} />
                    </ListItem>
                  </List>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Professional Information
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="Company" secondary={selectedRequest.company || 'Not provided'} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Department" secondary={selectedRequest.department || 'Not provided'} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Job Title" secondary={selectedRequest.job_title || 'Not provided'} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Expected Projects" secondary={selectedRequest.expected_project_count} />
                    </ListItem>
                  </List>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Access Request Details
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Reason for Access:
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {selectedRequest.reason_for_access}
                  </Typography>
                  
                  {selectedRequest.intended_use_case && (
                    <>
                      <Typography variant="subtitle2" color="text.secondary">
                        Intended Use Case:
                      </Typography>
                      <Typography variant="body2" paragraph>
                        {selectedRequest.intended_use_case}
                      </Typography>
                    </>
                  )}
                </Grid>

                {selectedRequest.risk_assessment && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Risk Assessment
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Chip 
                        label={`${selectedRequest.risk_assessment.level.toUpperCase()} RISK`}
                        color={getRiskColor(selectedRequest.risk_assessment.level)}
                      />
                      <Typography variant="body2">
                        Score: {selectedRequest.risk_assessment.score}/100
                      </Typography>
                    </Box>
                    
                    {selectedRequest.risk_assessment.factors.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Risk Factors:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                          {selectedRequest.risk_assessment.factors.map((factor, index) => (
                            <Chip key={index} label={factor} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Grid>
                )}

                {selectedRequest.audit_trail && (
                  <Grid item xs={12}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6">
                          <Timeline sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Audit Trail
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List>
                          {selectedRequest.audit_trail.map((audit, index) => (
                            <React.Fragment key={index}>
                              <ListItem>
                                <ListItemText
                                  primary={audit.action.replace('_', ' ').toUpperCase()}
                                  secondary={
                                    <Box>
                                      <Typography variant="body2">
                                        By: {audit.performed_by_name}
                                      </Typography>
                                      <Typography variant="caption">
                                        {format(new Date(audit.timestamp), 'MMM dd, yyyy HH:mm')}
                                      </Typography>
                                      {audit.reason && (
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                          Reason: {audit.reason}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                />
                              </ListItem>
                              {index < selectedRequest.audit_trail.length - 1 && <Divider />}
                            </React.Fragment>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            
            <DialogActions>
              {selectedRequest.status === 'pending' && !selectedRequest.is_expired && (
                <>
                  <Button 
                    color="error" 
                    onClick={() => setRejectionDialog(true)}
                    startIcon={<Cancel />}
                  >
                    Reject
                  </Button>
                  <Button 
                    color="success" 
                    variant="contained"
                    onClick={() => setApprovalDialog(true)}
                    startIcon={<CheckCircle />}
                  >
                    Approve
                  </Button>
                </>
              )}
              <Button onClick={() => setSelectedRequest(null)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Registration Request</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Are you sure you want to approve this registration request? This will create a user account 
            and send login credentials to the applicant.
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Admin Notes (Optional)"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="Add any notes about this approval..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={approveRequest} 
            variant="contained" 
            color="success"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {processing ? 'Approving...' : 'Approve & Create Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={rejectionDialog} onClose={() => setRejectionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Registration Request</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Please provide a reason for rejecting this registration request. 
            This will be sent to the applicant via email.
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Explain why this request is being rejected..."
            required
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Admin Notes (Optional)"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="Internal notes (not sent to applicant)..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={rejectRequest} 
            variant="contained" 
            color="error"
            disabled={!rejectionReason.trim() || processing}
            startIcon={processing ? <CircularProgress size={20} /> : <Cancel />}
          >
            {processing ? 'Rejecting...' : 'Reject Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminRegistrations;